import Papa from 'papaparse';

const ERRORS_MESSAGES = {
    encoding: (rowIndex) => `Invalid character at row ${rowIndex}. Make sure your CSV has UTF-8 encoding.`,
    headingEmpty: () => 'Found an empty heading.',
    headingLength: (heading) => `Heading "${heading}" is too long.`,
    headingUnique: (heading) => `Heading "${heading}" must be unique.`,
    headingNameRequired: () => `Heading name is required.`,
    headingEmailRequired: () => `Heading email is required.`,
    headingGroupRequired: () => `Headings group_id and group_role are required if any of them have been set.`,
    headingInvalid: (heading) =>
        `The field ${heading} could not be mapped, please refer to the example template and follow format as is in it, including case-folding.`,
    invalidColumns: (rowIndex) => `Wrong number of values at row ${rowIndex}.`,
    failedToProcess: (rowIndex, message) =>
        `It was not possible to process row ${rowIndex}.` + (message ? ` ${message}` : ''),
};

let errorMessage = '';

const handleError = (message, parser) => {
    errorMessage = message;
    parser.abort();
};

/**
 * @param {File} file
 */
export function validateUsersCsv(file) {
    errorMessage = '';

    return new Promise((resolve, reject) => {
        if (!file?.type || file.type !== 'text/csv') {
            reject({
                message: 'The file is not a CSV. Please use CSV file in order to proceed with import.',
            });
        }

        if (file.size > 100 * 1000 ** 2) {
            reject({
                message: 'Max file size is 100MB.',
            });
        }

        // Parse the CSV file and validate data.
        const groupFields = ['group_id', 'group_role'];
        let optionalHeadings = ['team_role', ...groupFields];
        let rowsCount = 0;

        const csvHeadings = [];

        Papa.parse(file, {
            delimiter: ',',
            worker: true,
            skipEmptyLines: true,
            step: (results, parser) => {
                rowsCount++;
                // Handle parser errors.
                if (results.errors.length) {
                    handleError(
                        ERRORS_MESSAGES.failedToProcess(results.errors[0].row, results.errors[0].message),
                        parser
                    );
                    return;
                }

                const data = results.data;

                // Check UTF-8 encoding.
                for (const value of data) {
                    // Check whether value has �.
                    // If it is so, it either means the file does not use UTF-8 or � is really present in value.
                    if (value && value.indexOf('�') >= 0) {
                        handleError(ERRORS_MESSAGES.encoding(rowsCount), parser);
                        return;
                    }
                }

                if (rowsCount === 1) {
                    // Validate headings.
                    if (!data.includes('name')) {
                        handleError(ERRORS_MESSAGES.headingNameRequired(), parser);
                        return;
                    }

                    if (!data.includes('email')) {
                        handleError(ERRORS_MESSAGES.headingEmailRequired(), parser);
                        return;
                    }

                    const hasAnyGroupField = groupFields.some((field) => data.includes(field));

                    if (hasAnyGroupField) {
                        const hasAllGroupFields = groupFields.every((field) => data.includes(field));

                        if (!hasAllGroupFields) {
                            handleError(ERRORS_MESSAGES.headingGroupRequired(), parser);
                            return;
                        }
                    }

                    const lcHeadingHash = {};

                    for (const heading of data) {
                        if (!heading) {
                            handleError(ERRORS_MESSAGES.headingEmpty(), parser);
                            return;
                        }

                        if (heading.length > 80) {
                            handleError(ERRORS_MESSAGES.headingLength(heading), parser);
                            return;
                        }

                        const lcHeading = heading.toLowerCase();

                        if (Object.keys(lcHeadingHash).includes(lcHeading)) {
                            handleError(ERRORS_MESSAGES.headingUnique(heading), parser);
                            return;
                        }

                        lcHeadingHash[lcHeading] = 1;

                        if (optionalHeadings.indexOf(lcHeading) >= 0) {
                            if (heading != lcHeading) {
                                handleError(ERRORS_MESSAGES.headingInvalid(heading), parser);
                                return;
                            }
                        }
                        csvHeadings.push(lcHeading);
                    }

                    return;
                }

                if (data.length !== csvHeadings.length) {
                    handleError(ERRORS_MESSAGES.invalidColumns(rowsCount), parser);
                    return;
                }
            },
            complete: () => {
                if (rowsCount <= 0 && !errorMessage) {
                    reject({
                        message:
                            'The CSV file does not contain any data. Please go back and make corrections to the file.',
                    });
                }

                resolve({
                    validCount: rowsCount > 1 ? rowsCount - 1 : 0,
                    message: errorMessage,
                });
            },
            error: () => {
                reject({
                    message: 'It was not possible to process the CSV file. Please contact support.',
                });
            },
        });
    });
}
